import { serverTimestamp } from "firebase/firestore";
import { authService } from "@/domain/services/authService";
import { userRepository } from "@/data/repositories/userRepository";
import { User } from "@/models/User";
import { writeAuthDebug } from "./authDebug";

export async function signInWithGoogle() {
  try {
    writeAuthDebug("auth_action:sign_in_clicked");
    const firebaseUser = await authService.signInWithGoogle();
    if (!firebaseUser) return null;

    const existingUser = await userRepository.getById(firebaseUser.uid);

    if (!existingUser) {
      const newUser: User = {
        id: firebaseUser.uid,
        email: firebaseUser.email ?? "",
        displayName: firebaseUser.displayName ?? "",
        photoURL: firebaseUser.photoURL ?? undefined,
        role: "player",

        userSettings: {
          theme: "classicCompact",
          autoAdvanceHole: false,
          focusMode: false,
          showNicknames: false,
          condenseScorecard: true,
          showActivityTicker: false,
          showEventPredictions: false
        },

        createdAt: serverTimestamp() as any,
        updatedAt: serverTimestamp() as any
      };

      await userRepository.create(newUser);
    }

    writeAuthDebug("auth_action:sign_in_complete", {
      firebaseUid: firebaseUser.uid,
      firebaseEmail: firebaseUser.email ?? null
    });
    return firebaseUser;
  } catch (err) {
    console.error("Google sign-in failed:", err);
    const code = err?.code as string | undefined;
    writeAuthDebug("auth_action:sign_in_error", {
      errorCode: code,
      errorMessage: err?.message
    });
    const host =
      typeof window !== "undefined" ? window.location.hostname : "this domain";

    let message = "Sign in failed. Please try again.";

    if (code === "auth/unauthorized-domain") {
      message = `Sign in is blocked for ${host}. Add this host to Firebase Authentication -> Settings -> Authorized domains.`;
    } else if (code === "auth/operation-not-allowed") {
      message =
        "Google sign-in is not enabled in Firebase Authentication -> Sign-in method.";
    } else if (code === "auth/network-request-failed") {
      message =
        "Network error during sign-in. Check Safari connectivity and try again.";
    } else if (code === "auth/web-storage-unsupported") {
      message =
        "This browser is blocking the storage Firebase needs for sign-in. Try Safari or Chrome with Private Browsing and content blockers turned off.";
    } else if (code === "auth/popup-blocked") {
      message =
        "Your browser blocked the sign-in window. On mobile, open the site in Safari or Chrome and try again.";
    }

    if (typeof window !== "undefined") {
      window.alert(message);
    }

    return null;
  }
}

export async function logout() {
  return authService.signOut();
}

export async function signInAsGuest() {
  return authService.signInAsGuest();
}
